import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { jsPDF } from 'jspdf';
import { Calculator, ClipboardCheck, Check, AlertCircle, FileText, Sparkles, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getPackages, getExtras } from '../data/pricingData';
import Logo from '../assets/Logo.svg';
import SEO from '../components/SEO';
import './Offerte.css';

let logoDataUrlCache = null;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const getLogoDataUrl = async () => {
  if (logoDataUrlCache) return logoDataUrlCache;
  const img = await loadImage(Logo);
  const canvas = document.createElement('canvas');
  canvas.width = 220;
  canvas.height = 44;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  logoDataUrlCache = canvas.toDataURL('image/png');
  return logoDataUrlCache;
};

const Offerte = () => {
  const { t } = useTranslation();
  
  // Memoize data to prevent unnecessary re-renders when t changes
  const packages = useMemo(() => getPackages(t), [t]);
  const extras = useMemo(() => getExtras(t), [t]);

  const [client, setClient] = useState({
    company: '',
    vat: '',
    contact: '',
    email: '',
    phone: '',
    projectTitle: t('offerte.default_project_title', 'Website traject'),
    notes: ''
  });

  const [selection, setSelection] = useState({
    packageId: 'business',
    maintenance: true,
    extras: {},
    quantities: {},
    customPrices: {},
    customItems: []
  });

  const [errors, setErrors] = useState({});
  const [generating, setGenerating] = useState(false);
  const [newCustomItem, setNewCustomItem] = useState({ description: '', price: '' });

  // Update default project title when language changes if user hasn't changed it
  useEffect(() => {
    setClient(prev => {
      // Check if title matches any known default translation
      const knownDefaults = ['Website traject', 'Website Project', 'Website project'];
      if (knownDefaults.includes(prev.projectTitle)) {
        return { ...prev, projectTitle: t('offerte.default_project_title', 'Website traject') };
      }
      return prev;
    });
  }, [t]);

  const handleAddCustomItem = () => {
    if (!newCustomItem.description.trim()) return;
    const price = parseFloat(newCustomItem.price) || 0;
    
    setSelection(prev => ({
      ...prev,
      customItems: [
        ...(prev.customItems || []),
        { 
          id: Date.now(), 
          description: newCustomItem.description, 
          price: price,
          quantity: 1 
        }
      ]
    }));
    setNewCustomItem({ description: '', price: '' });
  };

  const handleRemoveCustomItem = (id) => {
    setSelection(prev => ({
      ...prev,
      customItems: (prev.customItems || []).filter(item => item.id !== id)
    }));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.45, ease: 'easeOut' }
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('nl-BE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value);

  const formatAmount = (value) => value.toLocaleString('nl-BE');

  const validateVAT = (vat) => {
    const cleanVat = vat.replace(/[^0-9]/g, '');
    return cleanVat.length === 10; // accepteer 10 cijfers, met of zonder bez-prefix
  };

  const handleClientChange = (e) => {
    const { name, value } = e.target;
    setClient((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handlePackageSelect = (pkgId) => {
    setSelection((prev) => ({ ...prev, packageId: pkgId }));
  };

  const toggleMaintenance = () => {
    setSelection((prev) => ({ ...prev, maintenance: !prev.maintenance }));
  };

  const toggleExtra = (extraId) => {
    setSelection((prev) => {
      const active = prev.extras[extraId];
      const updatedExtras = { ...prev.extras, [extraId]: !active };
      if (!active) {
        return {
          ...prev,
          extras: updatedExtras,
          quantities: { ...prev.quantities, [extraId]: prev.quantities[extraId] || 1 },
          customPrices: { ...prev.customPrices }
        };
      }
      const { [extraId]: _removedQty, ...restQty } = prev.quantities;
      const { [extraId]: _removedPrice, ...restPrices } = prev.customPrices;
      return { ...prev, extras: updatedExtras, quantities: restQty, customPrices: restPrices };
    });
  };

  const handleQuantityChange = (extraId, value) => {
    const qty = Math.max(1, Number(value) || 1);
    setSelection((prev) => ({
      ...prev,
      quantities: { ...prev.quantities, [extraId]: qty }
    }));
  };

  const handleCustomPriceChange = (extraId, value) => {
    const clean = Math.max(0, Number(value) || 0);
    setSelection((prev) => ({
      ...prev,
      customPrices: { ...prev.customPrices, [extraId]: clean }
    }));
  };

  const selectedPackage = useMemo(
    () => packages.find((pkg) => pkg.id === selection.packageId) || packages[0],
    [selection.packageId, packages]
  );

  const totals = useMemo(() => {
    const standardExtrasTotal = extras.flatMap((group) => group.items).reduce((acc, item) => {
      if (!selection.extras[item.id]) return acc;
      const qty = selection.quantities[item.id] || 1;
      const basePrice = selection.customPrices[item.id] ?? item.price;
      return acc + basePrice * qty;
    }, 0);

    const customItemsTotal = (selection.customItems || []).reduce((acc, item) => {
      return acc + (item.price * (item.quantity || 1));
    }, 0);

    const extraTotals = standardExtrasTotal + customItemsTotal;

    const base = selectedPackage.price;
    const maintenance = selection.maintenance ? selectedPackage.maintenance.price : 0;
    const subtotal = base + extraTotals;
    const vat = subtotal * 0.21;
    const total = subtotal + vat;

    return {
      base,
      extrasTotal: extraTotals,
      maintenance,
      subtotal,
      vat,
      total
    };
  }, [selectedPackage, selection, extras]);

  const generatePdf = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;

    // Colors
    const primary = [249, 115, 22]; // #f97316
    const primaryLight = [255, 237, 213]; // #ffedd5
    const secondary = [15, 23, 42]; // #0f172a
    const textDark = [51, 65, 85]; // #334155
    const textLight = [100, 116, 139]; // #64748b
    const border = [226, 232, 240]; // #e2e8f0
    const white = [255, 255, 255];

    const today = new Date();
    const validUntil = new Date(today);
    validUntil.setDate(validUntil.getDate() + 15);
    const ref = `OFF-${today.getFullYear()}-${Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, '0')}`;

    // Helper functions
    const setFont = (type = 'normal', size = 10, color = textDark) => {
      doc.setFont('helvetica', type);
      doc.setFontSize(size);
      doc.setTextColor(...color);
    };

    // --- Header ---
    // Top accent bar
    doc.setFillColor(...primary);
    doc.rect(0, 0, pageWidth, 6, 'F');

    let y = 25;

    // Logo
    const tryLogo = await getLogoDataUrl().catch(() => null);
    if (tryLogo) {
      doc.addImage(tryLogo, 'PNG', margin, y, 50, 10);
    } else {
      setFont('bold', 20, secondary);
      doc.text('MyWebify', margin, y + 8);
    }

    // Company Info (Right side of header)
    setFont('bold', 24, primary);
    doc.text(t('offerte.pdf.title', 'OFFERTE'), pageWidth - margin, y + 8, { align: 'right' });

    y += 25;

    // Meta data grid
    const metaY = y;
    setFont('bold', 10, secondary);
    doc.text(t('offerte.pdf.reference', 'Referentie:'), pageWidth - margin - 50, metaY);
    doc.text(t('offerte.pdf.date', 'Datum:'), pageWidth - margin - 50, metaY + 6);
    doc.text(t('offerte.pdf.valid_until', 'Geldig tot:'), pageWidth - margin - 50, metaY + 12);

    setFont('normal', 10, textLight);
    doc.text(ref, pageWidth - margin, metaY, { align: 'right' });
    doc.text(today.toLocaleDateString('nl-BE'), pageWidth - margin, metaY + 6, { align: 'right' });
    doc.text(validUntil.toLocaleDateString('nl-BE'), pageWidth - margin, metaY + 12, { align: 'right' });

    // Client & Project Info (Left side)
    setFont('bold', 10, secondary);
    doc.text(t('offerte.pdf.to', 'Aan:'), margin, metaY);
    
    setFont('bold', 11, secondary);
    doc.text(client.company || 'Bedrijfsnaam', margin, metaY + 6);
    
    setFont('normal', 10, textDark);
    doc.text(client.contact || 'T.a.v. Contactpersoon', margin, metaY + 12);
    doc.text(client.email || 'email@bedrijf.be', margin, metaY + 18);
    if (client.vat) doc.text(client.vat, margin, metaY + 24);

    y = metaY + 40;

    // Project Title
    doc.setFillColor(...primaryLight);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 14, 2, 2, 'F');
    setFont('bold', 10, primary);
    doc.text(t('offerte.pdf.project', 'PROJECT:'), margin + 4, y + 9);
    setFont('bold', 10, secondary);
    doc.text(client.projectTitle || t('offerte.default_project_title', 'Website traject'), margin + 28, y + 9);

    y += 25;

    // --- Table ---
    const col1 = margin + 4; // Omschrijving
    const col2 = pageWidth - margin - 90; // Aantal
    const col3 = pageWidth - margin - 55; // Prijs
    const col4 = pageWidth - margin - 4; // Totaal (Right aligned)

    // Table Header
    doc.setFillColor(...secondary);
    doc.rect(margin, y, pageWidth - margin * 2, 10, 'F');
    setFont('bold', 9, white);
    doc.text(t('offerte.pdf.table.desc', 'OMSCHRIJVING'), col1, y + 7);
    doc.text(t('offerte.pdf.table.qty', 'AANTAL'), col2, y + 7, { align: 'center' });
    doc.text(t('offerte.pdf.table.price', 'PRIJS'), col3, y + 7, { align: 'right' });
    doc.text(t('offerte.pdf.table.total', 'TOTAAL'), col4, y + 7, { align: 'right' });

    y += 10;

    const drawRow = (desc, qty, price, total, isBold = false, isSub = false) => {
      // Page break check
      if (y > pageHeight - 50) {
        doc.addPage();
        y = margin + 10;
        // Redraw header
        doc.setFillColor(...secondary);
        doc.rect(margin, y, pageWidth - margin * 2, 10, 'F');
        setFont('bold', 9, white);
        doc.text(t('offerte.pdf.table.desc', 'OMSCHRIJVING'), col1, y + 7);
        doc.text(t('offerte.pdf.table.qty', 'AANTAL'), col2, y + 7, { align: 'center' });
        doc.text(t('offerte.pdf.table.price', 'PRIJS'), col3, y + 7, { align: 'right' });
        doc.text(t('offerte.pdf.table.total', 'TOTAAL'), col4, y + 7, { align: 'right' });
        y += 10;
      }

      const rowHeight = 10;
      
      if (isSub) {
         setFont('normal', 9, textLight);
         doc.text(`• ${desc}`, col1 + 4, y + 6);
      } else {
         setFont(isBold ? 'bold' : 'normal', 10, isBold ? secondary : textDark);
         doc.text(desc, col1, y + 6);
         doc.text(qty.toString(), col2, y + 6, { align: 'center' });
         doc.text(price === 0 ? t('offerte.free', 'Gratis') : formatCurrency(price), col3, y + 6, { align: 'right' });
         doc.text(total === 0 ? t('offerte.free', 'Gratis') : formatCurrency(total), col4, y + 6, { align: 'right' });
      }

      doc.setDrawColor(...border);
      doc.setLineWidth(0.1);
      doc.line(margin, y + rowHeight, pageWidth - margin, y + rowHeight);
      
      y += rowHeight;
    };

    // Package
    drawRow(selectedPackage.name, 1, selectedPackage.price, selectedPackage.price, true);
    
    // Features (sub-items)
    selectedPackage.features.slice(0, 4).forEach(feat => {
        drawRow(feat, '', 0, 0, false, true);
    });

    // Extras
    const activeExtras = extras.flatMap((group) => group.items).filter((item) => selection.extras[item.id]);
    activeExtras.forEach((item) => {
      const qty = selection.quantities[item.id] || 1;
      const basePrice = selection.customPrices[item.id] ?? item.price;
      drawRow(item.title, qty, basePrice, basePrice * qty);
    });

    // Custom Items
    (selection.customItems || []).forEach((item) => {
      drawRow(item.description, item.quantity || 1, item.price, item.price * (item.quantity || 1));
    });

    y += 10;

    // --- Totals ---
    if (y > pageHeight - 60) {
        doc.addPage();
        y = margin;
    }

    const totalsX = pageWidth - margin - 80;

    const drawTotalRow = (label, value, isBold = false, isLarge = false) => {
        setFont(isBold ? 'bold' : 'normal', isLarge ? 12 : 10, isLarge ? primary : textDark);
        doc.text(label, totalsX, y);
        doc.text(value, pageWidth - margin - 4, y, { align: 'right' });
        y += 8;
    };

    drawTotalRow(t('offerte.summary.subtotal', 'Subtotaal:'), formatCurrency(totals.subtotal));
    drawTotalRow(t('offerte.summary.vat', 'BTW (21%):'), formatCurrency(totals.vat));
    
    y += 2;
    doc.setDrawColor(...primary);
    doc.setLineWidth(0.5);
    doc.line(totalsX, y, pageWidth - margin, y);
    y += 6;

    drawTotalRow(t('offerte.summary.total', 'TOTAAL:'), formatCurrency(totals.total), true, true);

    if (selection.maintenance) {
        y += 4;
        setFont('normal', 9, textLight);
        doc.text(`+ ${t('offerte.summary.maintenance', 'Onderhoud')}: ${formatCurrency(totals.maintenance)} / ${t('prijzen.month', 'maand')}`, pageWidth - margin - 4, y, { align: 'right' });
    }

    // --- Footer ---
    const footerY = pageHeight - 20;
    doc.setDrawColor(...border);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY, pageWidth - margin, footerY);
    
    setFont('normal', 8, textLight);
    const footerText = 'MyWebify • België • BE 0123.456.789 • Info@mywebify.be';
    doc.text(footerText, pageWidth / 2, footerY + 8, { align: 'center' });
    doc.text(t('offerte.pdf.footer_terms', 'Op al onze diensten zijn onze algemene voorwaarden van toepassing.'), pageWidth / 2, footerY + 13, { align: 'center' });

    doc.save(`Offerte-${client.company.replace(/[^a-z0-9]/gi, '_') || 'MyWebify'}.pdf`);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!client.company.trim()) newErrors.company = t('offerte.form.error_company', 'Bedrijfsnaam is verplicht.');
    if (!client.contact.trim()) newErrors.contact = t('offerte.form.error_contact', 'Contactpersoon is verplicht.');
    if (!client.email.trim()) newErrors.email = t('offerte.form.error_email', 'E-mail is verplicht.');
    if (!client.vat.trim()) {
      newErrors.vat = t('offerte.form.error_vat_required', 'BTW-nummer is verplicht voor zakelijke klanten.');
    } else if (!validateVAT(client.vat)) {
      newErrors.vat = t('contact.form.vat_invalid', 'Ongeldig BTW-nummer. Formaat: BE0123.456.789 of 0123456789 (10 cijfers).');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setGenerating(true);
      await generatePdf();
    } catch (err) {
      console.error('PDF genereren mislukt', err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="offerte">
      <SEO 
        title={t('offerte.seo_title', 'Offerte Calculator')}
        description={t('offerte.seo_desc', 'Bereken direct de prijs voor uw nieuwe website. Stel uw pakket samen en ontvang een indicatieve offerte.')}
        canonical="/offerte"
      />
      <section className="offerte-hero">
        <div className="container">
          <div className="hero-grid">
            <motion.div
              className="hero-content"
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              <motion.span className="eyebrow" variants={itemVariants}>
                {t('offerte.hero.eyebrow', 'Offerte & Facturatie')}
              </motion.span>
              <motion.h1 className="page-title" variants={itemVariants}>
                {t('offerte.hero.title_1', 'Genereer Direct')} <br />
                <span className="text-gradient">{t('offerte.hero.title_2', 'Uw Offerte')}</span>
              </motion.h1>
              <motion.p className="page-subtitle" variants={itemVariants}>
                {t('offerte.hero.subtitle', 'Stel uw pakket samen, vink de modules aan en download direct een nette PDF voor uw klant.')}
              </motion.p>
              <motion.div className="hero-actions" variants={itemVariants}>
                <Link to="/prijzen" className="btn btn-secondary">
                   {t('offerte.hero.btn_prices', 'Bekijk prijsoverzicht')}
                </Link>
                <Link to="/contact" className="btn btn-primary">
                  {t('offerte.hero.btn_contact', 'Plan een gesprek')}
                </Link>
              </motion.div>
              <motion.div className="hero-meta" variants={itemVariants}>
                <div className="meta-chip">
                  <Sparkles size={16} />
                  {t('offerte.hero.meta_business', 'Business klanten met btw-nummer')}
                </div>
                <div className="meta-chip">
                  <Calculator size={16} />
                  {t('offerte.hero.meta_realtime', 'Realtime totaal incl. btw')}
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="hero-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            >
              <div className="hero-card-header">
                <div className="icon-badge">
                  <FileText size={22} />
                </div>
                <span>{t('offerte.summary_card.title', 'Samenvatting')}</span>
              </div>
              <div className="hero-card-body">
                <div className="hero-row">
                  <span>{t('offerte.summary_card.package', 'Pakket')}</span>
                  <strong>{selectedPackage.name}</strong>
                </div>
                <div className="hero-row">
                  <span>{t('offerte.summary_card.subtotal', 'Subtotaal (excl. btw)')}</span>
                  <strong>{formatCurrency(totals.subtotal)}</strong>
                </div>
                <div className="hero-row">
                  <span>{t('offerte.summary_card.vat', 'BTW 21%')}</span>
                  <strong>{formatCurrency(totals.vat)}</strong>
                </div>
                <div className="hero-row total">
                  <span>{t('offerte.summary_card.total', 'Totaal (incl. btw)')}</span>
                  <strong>{formatCurrency(totals.total)}</strong>
                </div>
              </div>
              <div className="hero-card-footer">
                <span>{t('offerte.summary_card.maintenance', 'Onderhoud')}</span>
                <strong>
                  {selection.maintenance
                    ? `${formatCurrency(totals.maintenance)} / ${t('prijzen.month', 'maand')}`
                    : t('offerte.summary_card.not_included', 'Niet inbegrepen')}
                </strong>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="offerte-builder section">
        <div className="container">
          <motion.div
            className="builder-grid two-col"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerVariants}
          >
            <motion.div className="builder-column" variants={itemVariants}>
              <div className="card builder-card">
                <div className="card-header">
                  <div className="card-title">
                    <span className="step-pill">{t('offerte.step_1.pill', 'Stap 1')}</span>
                    <Calculator size={18} />
                    <span>{t('offerte.step_1.title', 'Klant- en projectgegevens')}</span>
                  </div>
                  <p className="card-subtitle">{t('offerte.step_1.subtitle', 'Zakelijke klanten met btw-nummer')}</p>
                </div>

                <form className="builder-form" onSubmit={handleDownload}>
                  <div className="form-grid">
                    <label className="form-field">
                      <span>{t('offerte.form.company', 'Bedrijfsnaam')}*</span>
                      <input
                        name="company"
                        value={client.company}
                        onChange={handleClientChange}
                        placeholder={t('offerte.form.company', 'Bedrijfsnaam')}
                        className={errors.company ? 'input-error' : ''}
                        required
                      />
                      {errors.company && <small className="error-text">{errors.company}</small>}
                    </label>
                    <label className="form-field">
                      <span>{t('offerte.form.vat', 'BTW-nummer')}*</span>
                      <input
                        name="vat"
                        value={client.vat}
                        onChange={handleClientChange}
                        placeholder="BE0123.456.789"
                        className={errors.vat ? 'input-error' : ''}
                        required
                      />
                      {errors.vat && <small className="error-text">{errors.vat}</small>}
                    </label>
                  </div>

                  <div className="form-grid">
                    <label className="form-field">
                      <span>{t('offerte.form.contact', 'Contactpersoon')}*</span>
                      <input
                        name="contact"
                        value={client.contact}
                        onChange={handleClientChange}
                        placeholder={t('offerte.form.contact_ph', 'Naam')}
                        className={errors.contact ? 'input-error' : ''}
                        required
                      />
                      {errors.contact && <small className="error-text">{errors.contact}</small>}
                    </label>
                    <label className="form-field">
                      <span>{t('offerte.form.email', 'E-mail')}*</span>
                      <input
                        type="email"
                        name="email"
                        value={client.email}
                        onChange={handleClientChange}
                        placeholder="uw@email.be"
                        className={errors.email ? 'input-error' : ''}
                        required
                      />
                      {errors.email && <small className="error-text">{errors.email}</small>}
                    </label>
                  </div>

                  <label className="form-field">
                    <span>{t('offerte.form.phone', 'Telefoon')}</span>
                    <input
                      name="phone"
                      value={client.phone}
                      onChange={handleClientChange}
                      placeholder="+32 ..."
                    />
                  </label>

                  <label className="form-field">
                    <span>{t('offerte.form.project', 'Projecttitel')}</span>
                    <input
                      name="projectTitle"
                      value={client.projectTitle}
                      onChange={handleClientChange}
                      placeholder={t('offerte.default_project_title', 'Website traject')}
                    />
                  </label>

                  <label className="form-field">
                    <span>{t('offerte.form.notes', 'Opmerkingen')}</span>
                    <textarea
                      name="notes"
                      value={client.notes}
                      onChange={handleClientChange}
                      rows="4"
                      placeholder={t('offerte.form.notes_ph', 'Optioneel: specifieke wensen of afspraken')}
                    />
                  </label>

                  <div className="action-row">
                    <button type="submit" className="btn btn-primary" disabled={generating}>
                      {generating ? t('offerte.form.downloading', 'Bezig met downloaden...') : t('offerte.form.download', 'Download offerte (PDF)')}
                    </button>
                  </div>
                </form>
              </div>

              <div className="card option-card">
                <div className="card-header">
                  <div className="card-title">
                    <span className="step-pill">{t('offerte.step_2.pill', 'Stap 2')}</span>
                    <Sparkles size={18} />
                    <span>{t('offerte.step_2.title', 'Pakket & modules')}</span>
                  </div>
                  <p className="card-subtitle">{t('offerte.step_2.subtitle', 'Stel uw ideale bundel samen')}</p>
                </div>

                <div className="package-grid">
                  {packages.map((pkg) => (
                    <button
                      key={pkg.id}
                      type="button"
                      className={`package-card ${selection.packageId === pkg.id ? 'active' : ''}`}
                      onClick={() => handlePackageSelect(pkg.id)}
                    >
                      <div className="package-content-wrapper">
                        <div className="package-radio">
                          <div className={`radio-circle ${selection.packageId === pkg.id ? 'checked' : ''}`}>
                            {selection.packageId === pkg.id && <div className="radio-dot" />}
                          </div>
                        </div>
                        <div className="package-details">
                          <div className="package-header">
                            <div className="package-title-group">
                              <h3>{pkg.name}</h3>
                              {pkg.badge && <span className="package-badge-pill">{pkg.badge}</span>}
                            </div>
                            <div className="package-price">
                              € {formatAmount(pkg.price)}
                            </div>
                          </div>
                          
                          <p className="package-description">{pkg.description}</p>
                          
                          <ul className="package-features-compact">
                            {pkg.features.slice(0, 3).map((feat, idx) => (
                              <li key={idx}>
                                <Check size={14} className="check-icon" />
                                <span>{feat}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="maintenance-toggle">
                  <label className="toggle">
                    <input type="checkbox" checked={selection.maintenance} onChange={toggleMaintenance} />
                    <span className="slider" />
                    <span className="toggle-label">{t('offerte.toggle_maintenance', 'Onderhoud meerekenen')}</span>
                  </label>
                  <span className="toggle-note">{formatCurrency(selectedPackage.maintenance.price)} / {t('prijzen.month', 'maand')}</span>
                </div>

                <div className="extras">
                  <h4>{t('offerte.modules_title', 'Extra modules')}</h4>
                  <div className="extras-list">
                    {extras.map((group) => (
                      <div key={group.category} className="extras-group">
                        <div className="group-header">{group.category}</div>
                        {group.items.map((item) => {
                          const active = selection.extras[item.id];
                          const qty = selection.quantities[item.id] || 1;
                          const basePrice = selection.customPrices[item.id] ?? item.price;
                          return (
                            <div key={item.id} className={`extra-row ${active ? 'active' : ''}`}>
                              <label className="checkbox">
                                <input
                                  type="checkbox"
                                  checked={active || false}
                                  onChange={() => toggleExtra(item.id)}
                                />
                                <div className="checkbox-body">
                                  <div className="extra-title-row">
                                    <div className="extra-icon">{item.icon}</div>
                                    <div>
                                      <div className="extra-title">{item.title}</div>
                                      <div className="extra-desc">{item.description}</div>
                                    </div>
                                  </div>
                                  <div className="extra-meta">
                                    <span className="price-label">
                                      {item.isVariable ? `${t('prijzen.from', 'vanaf')} ${formatCurrency(item.price)}` : formatCurrency(item.price)}
                                      {item.unit ? ` · ${item.unit}` : ''}
                                    </span>
                                  </div>
                                </div>
                              </label>

                              {active && (
                                <div className="extra-controls">
                                  {item.unit && (
                                    <label className="small-field">
                                      <span>{t('offerte.qty', 'Aantal')}</span>
                                      <input
                                        type="number"
                                        min="1"
                                        value={qty}
                                        onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                      />
                                    </label>
                                  )}
                                  {item.isVariable && (
                                    <label className="small-field">
                                      <span>{t('offerte.amount', 'Bedrag')}</span>
                                      <input
                                        type="number"
                                        min="0"
                                        value={basePrice}
                                        onChange={(e) => handleCustomPriceChange(item.id, e.target.value)}
                                      />
                                    </label>
                                  )}
                                  <div className="line-total">
                                    <span>{t('offerte.total', 'Totaal')}</span>
                                    <strong>{formatCurrency(basePrice * (item.unit ? qty : 1))}</strong>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>

                  {/* Custom Items Section */}
                  <div className="custom-items-section">
                    <h4>{t('offerte.custom_items.title', 'Eigen toevoegingen')}</h4>
                    <div className="custom-items-list">
                      {(selection.customItems || []).map((item) => (
                        <div key={item.id} className="extra-row active">
                          <div className="checkbox-body">
                            <div className="extra-title-row">
                              <div className="extra-icon"><Sparkles size={20} /></div>
                              <div>
                                <div className="extra-title">{item.description}</div>
                                <div className="extra-desc">{t('offerte.custom_items.desc', 'Custom toevoeging')}</div>
                              </div>
                            </div>
                            <div className="extra-meta">
                              <span className="price-label">
                                {item.price === 0 ? t('offerte.free', 'Gratis') : formatCurrency(item.price)}
                              </span>
                            </div>
                          </div>
                          <button 
                            onClick={() => handleRemoveCustomItem(item.id)}
                            className="btn-icon-danger"
                            type="button"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="add-custom-item">
                      <div className="custom-input-group">
                        <input
                          type="text"
                          placeholder={t('offerte.custom_items.placeholder_desc', 'Omschrijving (bv. Logo ontwerp)')}
                          value={newCustomItem.description}
                          onChange={(e) => setNewCustomItem(prev => ({ ...prev, description: e.target.value }))}
                          className="custom-input"
                        />
                        <input
                          type="number"
                          placeholder={t('offerte.custom_items.placeholder_price', 'Prijs (€)')}
                          value={newCustomItem.price}
                          onChange={(e) => setNewCustomItem(prev => ({ ...prev, price: e.target.value }))}
                          className="custom-input price-input"
                        />
                        <button 
                          type="button" 
                          onClick={handleAddCustomItem}
                          className="btn btn-primary btn-sm"
                          disabled={!newCustomItem.description}
                        >
                          <Plus size={18} />
                          {t('offerte.custom_items.add', 'Toevoegen')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div className="builder-column aside" variants={itemVariants}>
              <div className="card summary-card">
                <div className="card-header">
                  <div className="card-title">
                    <span className="step-pill">{t('offerte.step_3.pill', 'Stap 3')}</span>
                    <ClipboardCheck size={18} />
                    <span>{t('offerte.step_3.title', 'Overzicht')}</span>
                  </div>
                  <p className="card-subtitle">{t('offerte.step_3.subtitle', 'Exclusief btw, automatisch incl. btw berekend')}</p>
                </div>

                <div className="summary-list">
                  <div className="summary-row">
                    <span>{t('offerte.summary.package', 'Pakket')} ({selectedPackage.name})</span>
                    <strong>{formatCurrency(totals.base)}</strong>
                  </div>
                  <div className="summary-row">
                    <span>{t('offerte.summary.modules', 'Extra modules')}</span>
                    <strong>{formatCurrency(totals.extrasTotal)}</strong>
                  </div>
                  <div className="summary-row">
                    <span>{t('offerte.summary.subtotal', 'Subtotaal')}</span>
                    <strong>{formatCurrency(totals.subtotal)}</strong>
                  </div>
                  <div className="summary-row">
                    <span>{t('offerte.summary.vat', 'BTW 21%')}</span>
                    <strong>{formatCurrency(totals.vat)}</strong>
                  </div>
                  <div className="summary-row total">
                    <span>{t('offerte.summary.total', 'Totaal incl. btw')}</span>
                    <strong>{formatCurrency(totals.total)}</strong>
                  </div>
                </div>

                <div className="maintenance-box">
                  <div>
                    <p className="small-label">{t('offerte.summary.maintenance_opt', 'Onderhoud (optioneel)')}</p>
                    <strong>{formatCurrency(totals.maintenance)} / {t('prijzen.month', 'maand')}</strong>
                    <p className="muted">{t('offerte.summary.maintenance_note', 'Activeer of deactiveer bij de modules.')}</p>
                  </div>
                </div>

                <div className="summary-actions">
                  <button className="btn btn-primary" onClick={handleDownload} disabled={generating}>
                    {generating ? t('offerte.form.downloading', 'Bezig met downloaden...') : t('offerte.form.download', 'Download offerte (PDF)')}
                  </button>
                </div>
              </div>

              <div className="card notice-card">
                <div className="notice-icon">
                  <AlertCircle size={18} />
                </div>
                <div>
                  <h4>{t('offerte.how_works.title', 'Hoe werkt het?')}</h4>
                  <p>
                    {t('offerte.how_works.text', '1) Kies pakket. 2) Vink modules aan. 3) Download PDF. Bedragen zijn excl. btw; 21% wordt automatisch meegerekend.')}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Offerte;
